// Link Co-Parents — admin tool to fix Phase B auto-link misses.
//
// Phase B (auto-detect on sign-in) only fires if Parent 1 already had
// guardian2Email/Phone set on their user doc BEFORE Parent 2 first signed in.
// When that ordering breaks (common case: Parent 2 signed up first, Parent 1
// only added the guardian2 fields later), the link never happens.
//
// This page scans all users with guardian2Email/Phone set, checks for matching
// user accounts that AREN'T already linked, and shows them as actionable rows.
// Admin clicks "Link" → adminLinkCoParent CF re-points athletes.
//
// Also shows audit history from the coParentLinks collection (both
// user-driven via /co-parent-link AND admin-driven via this page).

import React, { useEffect, useState, useMemo } from 'react';
import { db } from '../firebase';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import Layout from '../components/Layout';
import {
  Box, Typography, Paper, Button, Alert, CircularProgress, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Tabs, Tab, Divider, Stack,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import HistoryIcon from '@mui/icons-material/History';
import SearchIcon from '@mui/icons-material/Search';

const DARK = '#1E3A5F';

// Normalize an email or phone for comparison
const normEmail = (s) => (s || '').toLowerCase().trim();
const normPhone = (s) => (s || '').replace(/\D/g, '');

function LinkCoParents() {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Scenario B matches: guardian1 has guardian2Email/Phone set AND a matching
  // user account exists AND that user is NOT already linked to g1's athletes.
  const [matches, setMatches] = useState([]);

  // Audit log of existing links
  const [audit, setAudit] = useState([]);

  // In-flight link operation
  const [linkingPairKey, setLinkingPairKey] = useState(null);

  const userMapByEmail = useMemo(() => new Map(), []);
  const userMapByPhone = useMemo(() => new Map(), []);
  const userMapByUid = useMemo(() => new Map(), []);

  // Load everything: users (for matching), athletes (for parentUserIds counts),
  // and coParentLinks (for audit tab).
  const runScan = async () => {
    setScanning(true);
    setError('');
    setSuccess('');
    userMapByEmail.clear();
    userMapByPhone.clear();
    userMapByUid.clear();
    try {
      const [usersSnap, athletesSnap, auditSnap] = await Promise.all([
        getDocs(collection(db, 'users')),
        getDocs(collection(db, 'athletes')),
        getDocs(collection(db, 'coParentLinks')),
      ]);

      // Index users by their normalized auth email and phone for fast lookup
      const allUsers = usersSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      allUsers.forEach(u => {
        userMapByUid.set(u.id, u);
        const e = normEmail(u.email);
        if (e) userMapByEmail.set(e, u);
        const p = normPhone(u.phone);
        if (p) userMapByPhone.set(p, u);
      });

      // Map: guardian1 UID → set of athlete UIDs in parentUserIds
      const g1ToAthletes = new Map();
      athletesSnap.docs.forEach(d => {
        const a = d.data();
        if (a.isArchived || a.mergedInto) return;
        const parents = Array.isArray(a.parentUserIds)
          ? a.parentUserIds
          : a.parentUserId ? [a.parentUserId] : [];
        parents.forEach(uid => {
          if (!g1ToAthletes.has(uid)) g1ToAthletes.set(uid, []);
          g1ToAthletes.get(uid).push({ id: d.id, ...a });
        });
      });

      // Find scenario B matches
      const found = [];
      allUsers.forEach(g1 => {
        const g2EmailRaw = g1.guardian2Email || g1.p2Email;
        const g2PhoneRaw = g1.guardian2Phone || g1.p2Phone;
        const g2Email = normEmail(g2EmailRaw);
        const g2Phone = normPhone(g2PhoneRaw);
        if (!g2Email && !g2Phone) return;

        // Find a matching user account by email or phone
        let matchedUser = null;
        let matchedBy = null;
        if (g2Email && userMapByEmail.has(g2Email)) {
          matchedUser = userMapByEmail.get(g2Email);
          matchedBy = 'email';
        } else if (g2Phone && userMapByPhone.has(g2Phone)) {
          matchedUser = userMapByPhone.get(g2Phone);
          matchedBy = 'phone';
        }
        if (!matchedUser) return;             // no matching account yet
        if (matchedUser.id === g1.id) return; // self-link guard

        // Get the athletes linked to g1 — if matchedUser is already in all of
        // them, nothing to do; otherwise show as actionable.
        const g1Athletes = g1ToAthletes.get(g1.id) || [];
        if (g1Athletes.length === 0) return;
        const unlinkedAthletes = g1Athletes.filter(a => {
          const parents = Array.isArray(a.parentUserIds)
            ? a.parentUserIds
            : a.parentUserId ? [a.parentUserId] : [];
          return !parents.includes(matchedUser.id);
        });
        if (unlinkedAthletes.length === 0) return; // already fully linked

        found.push({
          g1: { id: g1.id, firstName: g1.firstName || '', lastName: g1.lastName || '', email: g1.email || '' },
          g2: { id: matchedUser.id, firstName: matchedUser.firstName || '', lastName: matchedUser.lastName || '', email: matchedUser.email || '' },
          matchedBy,
          guardian2EmailOnG1: g2EmailRaw || '',
          guardian2PhoneOnG1: g2PhoneRaw || '',
          unlinkedAthleteCount: unlinkedAthletes.length,
          unlinkedAthleteNames: unlinkedAthletes.map(a => `${a.firstName || ''} ${a.lastName || ''}`.trim()),
        });
      });
      setMatches(found);

      // Build audit list, newest first
      const auditRows = auditSnap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => {
          const ta = a.linkedAt?.seconds || 0;
          const tb = b.linkedAt?.seconds || 0;
          return tb - ta;
        });
      // Enrich with user display names
      auditRows.forEach(r => {
        const g1 = userMapByUid.get(r.guardian1Uid);
        const g2 = userMapByUid.get(r.coParentUid);
        r._g1Name = g1 ? `${g1.firstName || ''} ${g1.lastName || ''}`.trim() : r.guardian1Email || r.guardian1Uid;
        r._g2Name = g2 ? `${g2.firstName || ''} ${g2.lastName || ''}`.trim() : r.coParentEmail || r.coParentUid;
      });
      setAudit(auditRows);
    } catch (e) {
      console.error('Scan failed:', e);
      setError('Failed to scan: ' + (e.message || 'Unknown error'));
    } finally {
      setScanning(false);
      setLoading(false);
    }
  };

  useEffect(() => { runScan(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLink = async (m) => {
    const pairKey = `${m.g1.id}__${m.g2.id}`;
    setLinkingPairKey(pairKey);
    setError('');
    setSuccess('');
    try {
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'adminLinkCoParent');
      const result = await fn({ guardian1Uid: m.g1.id, coParentUid: m.g2.id });
      const data = result.data || {};
      setSuccess(
        `Linked ${data.linkedAthleteCount || 0} athlete${data.linkedAthleteCount === 1 ? '' : 's'}: ` +
        `${(data.athleteNames || []).join(', ')}`
      );
      // Refresh the scan to reflect the new state
      await runScan();
    } catch (e) {
      console.error('Link failed:', e);
      setError('Failed to link: ' + (e.message || 'Unknown error'));
    } finally {
      setLinkingPairKey(null);
    }
  };

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
          <LinkIcon sx={{ color: DARK }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: DARK }}>
            Link Co-Parents
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Find users where Guardian 1 set a co-parent email/phone, AND a matching account exists,
          but the accounts aren't linked yet. Click <strong>Link</strong> to add the co-parent's UID to all of Guardian 1's athletes.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

        <Paper elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #eee', px: 2 }}>
            <Tab icon={<SearchIcon fontSize="small" />} iconPosition="start" label={`Pending Matches (${matches.length})`} />
            <Tab icon={<HistoryIcon fontSize="small" />} iconPosition="start" label={`Audit Log (${audit.length})`} />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : tab === 0 ? (
              <>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Typography variant="body2" sx={{ color: '#666' }}>
                    {matches.length === 0
                      ? 'No pending matches found — every guardian2Email/Phone that resolves to an account is already linked.'
                      : `${matches.length} co-parent ${matches.length === 1 ? 'match needs' : 'matches need'} linking.`}
                  </Typography>
                  <Button size="small" onClick={runScan} disabled={scanning}>
                    {scanning ? 'Scanning…' : 'Re-scan'}
                  </Button>
                </Stack>

                {matches.length > 0 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Guardian 1</strong></TableCell>
                          <TableCell><strong>Co-Parent (matched account)</strong></TableCell>
                          <TableCell><strong>Match On</strong></TableCell>
                          <TableCell><strong>Athletes</strong></TableCell>
                          <TableCell align="right"></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {matches.map(m => {
                          const pairKey = `${m.g1.id}__${m.g2.id}`;
                          const inFlight = linkingPairKey === pairKey;
                          return (
                            <TableRow key={pairKey}>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {`${m.g1.firstName} ${m.g1.lastName}`.trim() || '(no name)'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{m.g1.email}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {`${m.g2.firstName} ${m.g2.lastName}`.trim() || '(no name)'}
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{m.g2.email}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={m.matchedBy}
                                  sx={{
                                    backgroundColor: m.matchedBy === 'email' ? '#E9F7EF' : '#FEF9E7',
                                    color: m.matchedBy === 'email' ? '#1E6B3C' : '#7B4800',
                                  }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {m.unlinkedAthleteCount} to link
                                </Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>
                                  {m.unlinkedAthleteNames.join(', ')}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<LinkIcon />}
                                  disabled={inFlight}
                                  onClick={() => handleLink(m)}
                                  sx={{ backgroundColor: DARK, '&:hover': { backgroundColor: '#152C47' } }}
                                >
                                  {inFlight ? 'Linking…' : 'Link'}
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            ) : (
              <>
                <Typography variant="body2" sx={{ color: '#666', mb: 2 }}>
                  History of every co-parent link, including user-driven (via the sign-in detection flow)
                  and admin-driven (via this page).
                </Typography>
                {audit.length === 0 ? (
                  <Typography variant="body2" sx={{ color: '#888', py: 4, textAlign: 'center' }}>
                    No co-parent links yet.
                  </Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>When</strong></TableCell>
                          <TableCell><strong>Guardian 1</strong></TableCell>
                          <TableCell><strong>Co-Parent</strong></TableCell>
                          <TableCell><strong>Matched By</strong></TableCell>
                          <TableCell><strong>Athletes</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {audit.map(r => {
                          const when = r.linkedAt?.seconds
                            ? new Date(r.linkedAt.seconds * 1000).toLocaleString()
                            : '—';
                          const chipColor =
                            r.matchedBy === 'admin' ? { bg: '#FDEDEC', color: '#922B21' } :
                            r.matchedBy === 'email' ? { bg: '#E9F7EF', color: '#1E6B3C' } :
                            r.matchedBy === 'phone' ? { bg: '#FEF9E7', color: '#7B4800' } :
                            { bg: '#EBF5FB', color: '#1A5276' };
                          return (
                            <TableRow key={r.id}>
                              <TableCell>
                                <Typography variant="caption">{when}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{r._g1Name}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{r.guardian1Email}</Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{r._g2Name}</Typography>
                                <Typography variant="caption" sx={{ color: '#888' }}>{r.coParentEmail}</Typography>
                              </TableCell>
                              <TableCell>
                                <Chip
                                  size="small"
                                  label={r.matchedBy || '—'}
                                  sx={{ backgroundColor: chipColor.bg, color: chipColor.color }}
                                />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {(r.athleteIds || []).length}
                                </Typography>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        </Paper>
      </Box>
    </Layout>
  );
}

export default LinkCoParents;
